import { useEffect, useMemo } from 'react';
import { toMajorCurrency } from '@zonofi/common';
import {
  TurnoverInvoiceItemView,
  TurnoverInvoiceOverviewData,
  TurnoverPartyDetails,
  VALIDATION_STATUS_MAP,
  getWorkspaceId,
  useAppSelector,
  useLazyGetTurnoverInvoiceDetailsQuery,
} from '@zonofi/zono-money-store';
import { MappingStatus } from '../turnover/turnover-details/status-badge/status-badge';
// import { formatDate } from '@zonofi/shared';

const formatDate = (value?: string | null): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatText = (value?: string | null): string => {
  // console.log('formatText', value, typeof value);
  if (!value || typeof value === 'object') return '-';

  const next = value?.trim();

  return next ? next : '-';
};

const toMajorNumber = (value?: number | null): number => {
  if (value == null) return 0;
  return Number(toMajorCurrency(Number(value))) || 0;
};

const mapLineStatus = (status?: string | null): MappingStatus => {
  if (status === 'MAPPED') return 'mapped';
  if (status === 'MAPPED_BRAND_NOT_FOUND') return 'brand_not_found';
  return 'unmapped';
};

export const useTurnoverInvoiceDetails = (invoiceId?: string) => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const [fetchInvoiceDetails, invoiceDetailsResult] =
    useLazyGetTurnoverInvoiceDetailsQuery();

  useEffect(() => {
    if (!workspaceId || !invoiceId) return;

    fetchInvoiceDetails({
      sellerWorkspaceId: workspaceId,
      invoiceId,
    });
  }, [fetchInvoiceDetails, invoiceId, workspaceId]);

  const invoice = invoiceDetailsResult.data?.turnoverinvoice?.[0];
  const invoiceExtra = invoiceDetailsResult.data?.turnoverinvoice_details?.[0];

  const distributor = useMemo<TurnoverPartyDetails>(() => {
    const matched =
      invoice?.turnoverinvoice_detail?.matched_distributor_details;
    const details = invoiceExtra?.distributordetails;
    const fallback = invoice?.turnoverinvoice_detail?.distributordetails;

    return {
      name: formatText(
        matched?.matched_data?.supplierName ??
          details?.name ??
          fallback?.name ??
          invoice?.sellername ??
          invoiceExtra?.company_name
      ),
      phone: formatText(
        matched?.matched_data?.phone ??
          details?.phone_number ??
          fallback?.phone_number
      ),
      email: formatText(
        matched?.matched_data?.email ?? details?.email ?? fallback?.email
      ),
      address: formatText(
        matched?.matched_data?.physicalAddress?.address1 ??
          details?.address ??
          fallback?.address
      ),
      gst: formatText(
        matched?.matched_data?.GSTNumber ?? details?.gstin ?? fallback?.gstin
      ),
    };
  }, [invoice, invoiceExtra]);

  const buyer = useMemo<TurnoverPartyDetails>(() => {
    const matched = invoice?.turnoverinvoice_detail?.matched_buyer_details;
    const details = invoiceExtra?.buyerdetails;
    const fallback = invoice?.turnoverinvoice_detail?.buyerdetails;

    return {
      name: formatText(
        matched?.matched_data?.name ??
          details?.name ??
          fallback?.name ??
          invoice?.buyername
      ),
      phone: formatText(
        matched?.matched_data?.phone ??
          details?.phone_number ??
          fallback?.phone_number
      ),
      email: formatText(
        matched?.matched_data?.email ?? details?.email ?? fallback?.email
      ),
      address: formatText(
        matched?.matched_data?.physicalAddress?.address1 ??
          details?.address ??
          fallback?.address
      ),
      gst: formatText(
        matched?.matched_data?.GSTNumber ?? details?.gstin ?? fallback?.gstin
      ),
    };
  }, [invoice, invoiceExtra]);

  const overview = useMemo<TurnoverInvoiceOverviewData>(() => {
    const mappedProductsSum =
      invoice?.mapped_products_aggregate?.aggregate?.sum
        ?.total_amount_with_tax ??
      invoice?.mapped_products_aggregate?.aggregate?.sum?.total_amount ??
      0;
    const contributionSumAgg =
      invoice?.contribution_lines_aggregate?.aggregate?.sum;
    const contributionValue =
      contributionSumAgg?.contribution != null
        ? contributionSumAgg.contribution
        : (contributionSumAgg?.total_amount_with_tax ??
          contributionSumAgg?.total_amount ??
          0);

    return {
      totalInvoiceValue: toMajorNumber(invoice?.invoice_amount),
      ourProductsValue: toMajorNumber(mappedProductsSum),
      contributionValue: toMajorNumber(contributionValue),
      distributor,
      buyer,
      submittedByName: formatText(invoice?.submittedby ?? invoice?.sellername),
      submittedOn: formatDate(invoice?.created_at),
      invoiceUrl: null,
    };
  }, [buyer, distributor, invoice]);

  const invoiceItems = useMemo<TurnoverInvoiceItemView[]>(() => {
    return (invoice?.turnoverinvoice_lines ?? []).map((line) => {
      const contributionAmount =
        line?.contribution != null
          ? toMajorNumber(line.contribution)
          : line?.scheme_id
            ? toMajorNumber(line.total_amount_with_tax ?? line.total_amount)
            : 0;

      return {
        id: line.id,
        name:
          formatText(line.mapped_product_name) !== '-'
            ? formatText(line.mapped_product_name)
            : formatText(line.product_name),
        price: toMajorNumber(line.unit_price),
        quantity: Number(line.quantity ?? 0),
        discount: Number(line.discount ?? 0),
        totalAmount: toMajorNumber(line.total_amount),
        contributionAmount,
        appliedScheme:
          formatText(line.turnover_scheme?.name) !== '-'
            ? formatText(line.turnover_scheme?.name)
            : formatText(line.turnover_scheme?.code),
        sku: formatText(line.sku_code),
        hsn: formatText(line.hsn_code),
        status: mapLineStatus(line.status),
      };
    });
  }, [invoice?.turnoverinvoice_lines]);

  const validationStatus = useMemo(() => {
    const raw =
      invoice?.invoice_validation_status?.[0]?.validation_status ??
      invoice?.validationStatus ??
      null;
    return raw ? (VALIDATION_STATUS_MAP[raw] ?? raw) : null;
  }, [invoice?.invoice_validation_status, invoice?.validationStatus]);

  return {
    invoiceId: invoice?.id ?? invoiceId ?? '',
    invoiceNumber: formatText(invoice?.invoice_number),
    invoiceDate: formatDate(invoice?.invoice_date),
    invoiceSkuCount:
      Number(invoice?.turnoverinvoice_lines_aggregate?.aggregate?.count ?? 0) ||
      invoiceItems.length,
    invoiceStatus: formatText(invoice?.status),
    validationStatus,
    overview,
    invoiceItems,
    isLoading:
      invoiceDetailsResult.isLoading || invoiceDetailsResult.isFetching,
    isError: invoiceDetailsResult.isError,
    error: invoiceDetailsResult.error,
    refetch: () => {
      if (!workspaceId || !invoiceId) return;
      fetchInvoiceDetails({
        sellerWorkspaceId: workspaceId,
        invoiceId,
      });
    },
  };
};
